import { useEffect, useState } from 'react';
import type { TourStep } from '@/types/product-tour';

interface TourSpotlightProps {
  step: TourStep;
  isActive: boolean;
}

export function TourSpotlight({ step, isActive }: TourSpotlightProps) {
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);

  useEffect(() => {
    if (!isActive || !step.target) {
      setTargetRect(null);
      return;
    }

    const updateTargetRect = () => {
      const element = document.querySelector(step.target!);
      if (element) {
        setTargetRect(element.getBoundingClientRect());
      }
    };

    // Only scroll if the element is not already fully visible in the viewport
    const element = document.querySelector(step.target!);
    if (element) {
      const rect = element.getBoundingClientRect();
      const isVisible =
        rect.top >= 0 &&
        rect.bottom <= window.innerHeight &&
        rect.left >= 0 &&
        rect.right <= window.innerWidth;
      if (!isVisible) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }

    // Initial update
    updateTargetRect();

    // Update on scroll and resize
    window.addEventListener('scroll', updateTargetRect, true);
    window.addEventListener('resize', updateTargetRect);

    return () => {
      window.removeEventListener('scroll', updateTargetRect, true);
      window.removeEventListener('resize', updateTargetRect);
    };
  }, [step.target, isActive]);

  if (!isActive) {
    return null;
  }

  // No target — render a full-screen backdrop only (centre modal steps)
  if (!targetRect) {
    return (
      <div
        className="fixed inset-0 bg-black/30 transition-opacity duration-300 pointer-events-none"
        style={{ zIndex: 9998 }}
      />
    );
  }

  const padding = step.spotlightPadding || 8;

  return (
    <>
      {/* Spotlight cutout — box shadow creates the overlay, leaving the target uncovered */}
      <div
        className="fixed transition-all duration-300 pointer-events-none"
        style={{
          zIndex: 9999,
          top: targetRect.top - padding,
          left: targetRect.left - padding,
          width: targetRect.width + padding * 2,
          height: targetRect.height + padding * 2,
          boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.25)',
          borderRadius: '8px',
        }}
      />

      {/* Teal border around highlighted element */}
      <div
        className="fixed transition-all duration-300 pointer-events-none animate-pulse"
        style={{
          zIndex: 10000,
          top: targetRect.top - padding,
          left: targetRect.left - padding,
          width: targetRect.width + padding * 2,
          height: targetRect.height + padding * 2,
          border: '2px solid #2dd4bf',
          borderRadius: '8px',
        }}
      />
    </>
  );
}
